import type {CreateAutomationFormValues} from "./schema";

export interface CreateEmailAutomationPayload {
  automationName: string;
  subject: {
    template: string;
    variables: string[];
  };
  body: {
    template: string;
    variables: string[];
  };
  customButton: boolean;
  buttonText: string;
  buttonLink: string;
  trigger: "after-purchase" | "abandoned-purchase";
  sendTime: "immediate" | "custom";
  delayTime: number;
  offerings: {
    offeringId: number;
    offeringName: string;
  }[];
  draft?: boolean;
}

export interface EmailAutomationOfferingOption {
  value: string;
  label: string;
  offeringId: number;
  offeringName: string;
}

const SHORT_CODE_MAP: Record<string, string> = {
  "{Customer_name}": "{{customerName}}",
  "{Coach_name}": "{{coachName}}",
  "{Coach_email}": "{{coachEmail}}",
  "{Offering_name}": "{{offeringName}}",
};

function replaceShortCodes(text: string): string {
  let result = text;
  for (const [uiCode, apiCode] of Object.entries(SHORT_CODE_MAP)) {
    result = result.split(uiCode).join(apiCode);
  }
  return result;
}

function extractVariables(template: string): string[] {
  const regex = /\{\{(\w+)\}\}/g;
  const vars = new Set<string>();
  let match;
  while ((match = regex.exec(template)) !== null) {
    vars.add(match[1]);
  }
  return Array.from(vars);
}

function convertDelayToHours(
  value: number | undefined,
  unit: string | undefined
): number {
  if (!value || !unit) return 0;
  const v = Number(value);
  if (unit === "minutes") return Math.round((v / 60) * 100) / 100;
  if (unit === "hours") return v;
  if (unit === "days") return v * 24;
  return 0;
}

export function transformFormToApiPayload(
  form: CreateAutomationFormValues,
  draft: boolean,
  availableOfferings: EmailAutomationOfferingOption[]
): CreateEmailAutomationPayload {
  const subjectTemplate = replaceShortCodes(form.subject);
  const bodyTemplate = replaceShortCodes(form.body);

  const trigger =
    form.trigger === "after_purchase" ? "after-purchase" : "abandoned-purchase";

  const isAbandoned = form.trigger === "abandoned_purchase";

  const sendTime = isAbandoned
    ? "immediate"
    : form.sendTiming !== "immediately"
    ? "custom"
    : "immediate";

  const delayTime = isAbandoned
    ? 0
    : sendTime === "immediate"
    ? 0
    : convertDelayToHours(form.delayValue, form.delayUnit);

  const offerings = (form.offerings ?? [])
    .map((selectedOfferingId) => {
      const matchingOffering = availableOfferings.find(
        (offering) => offering.value === String(selectedOfferingId)
      );

      if (matchingOffering) {
        return {
          offeringId: matchingOffering.offeringId,
          offeringName: matchingOffering.offeringName,
        };
      }

      const numericId = Number(selectedOfferingId);
      if (!Number.isNaN(numericId)) {
        return {
          offeringId: numericId,
          offeringName: String(selectedOfferingId),
        };
      }

      return null;
    })
    .filter(
      (offering): offering is CreateEmailAutomationPayload["offerings"][number] =>
        offering !== null
    );

  return {
    automationName: form.automationName,
    subject: {
      template: subjectTemplate,
      variables: extractVariables(subjectTemplate),
    },
    body: {
      template: bodyTemplate,
      variables: extractVariables(bodyTemplate),
    },
    customButton: form.addCustomButton ?? false,
    buttonText: form.buttonText ?? "",
    buttonLink: form.buttonLink ?? "",
    trigger,
    sendTime,
    delayTime,
    offerings,
    draft,
  };
}
